// @ts-nocheck
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Search, ChevronDown, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { FormBuilder } from "./FormBuilder";
import { DEFAULT_MEDIA_FIELDS, type EventData, type MediaCredential } from "@/types/events";

type Tab = "form";

interface Props {
  event: EventData;
  updateEvent: (patch: Partial<EventData>) => void;
  eventId: string | undefined;
}

export function MediaCredentialsSection({ event, updateEvent, eventId }: Props) {
  const [tab, setTab] = useState<Tab>("form");
  const enabled = !!event.media_credentials_enabled;
  const paid = !!event.media_credentials_paid;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold tracking-tight">MEDIA CREDENTIALS</h2>
        <p className="text-sm text-muted-foreground mt-1">Press and media credential applications.</p>
      </div>

      <div className="space-y-3 p-4 rounded-lg border border-border bg-card">
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox checked={enabled} onCheckedChange={(v) => updateEvent({ media_credentials_enabled: !!v })} className="mt-0.5" />
          <span className="text-sm font-medium">Enable media credentials for this event</span>
        </label>
        {enabled && (
          <>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={paid} onCheckedChange={(v) => updateEvent({ media_credentials_paid: !!v })} className="mt-0.5" />
              <span className="text-sm font-medium">Charge for media credentials</span>
            </label>
            {paid && (
              <div className="max-w-xs space-y-1.5">
                <Label>Credential price</Label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                  <Input
                    type="number"
                    min={0}
                    max={1000}
                    step="0.01"
                    inputMode="decimal"
                    className="pl-7"
                    value={event.media_credentials_price ?? 0}
                    onChange={(e) => {
                      const n = parseFloat(e.target.value);
                      const clamped = Number.isFinite(n) ? Math.min(Math.max(n, 0), 1000) : 0;
                      updateEvent({ media_credentials_price: clamped });
                    }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Enter dollars, not cents. Each requester will be charged{" "}
                  <span className="font-semibold text-foreground">
                    ${Number(event.media_credentials_price ?? 0).toFixed(2)}
                  </span>
                  .
                </p>
                {Number(event.media_credentials_price ?? 0) > 500 && (
                  <p className="text-xs font-medium text-amber-600">
                    That's over $500 — did you mean ${(Number(event.media_credentials_price) / 100).toFixed(2)}?
                  </p>
                )}
              </div>
            )}

          </>
        )}
      </div>

      {!enabled || !eventId ? (
        <div className="p-8 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">
          {!eventId ? "Save the event first to manage media credentials." : "Enable media credentials to configure the application form."}
        </div>
      ) : (
        <FormBuilder
          eventId={eventId}
          formType="media_credentials"
          defaultFields={DEFAULT_MEDIA_FIELDS}
          defaultTitle="Media Credential Application"
          defaultDescription="Apply for a media credential to cover this event. Applications are reviewed within 48 hours."
          defaultThankYou="Thanks — your application has been submitted. We'll review it within 48 hours."
        />
      )}
    </div>
  );
}

/* ───── Requests ─────
 * Payment approves a credential outright, so nothing sits in "pending" any
 * more — every non-denied request belongs on this screen. It reads through the
 * sheet-tab edge function because media_credentials is only readable by admins
 * and active org members; a direct browser read returns zero rows with a 200
 * for anyone else, which is how this screen used to report nothing.
 */
export function PendingTab({ eventId, paid }: { eventId: string; paid: boolean }) {
  const [rows, setRows] = useState<MediaCredential[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [denying, setDenying] = useState<MediaCredential | null>(null);
  const [denialReason, setDenialReason] = useState("");
  const [reviewers, setReviewers] = useState<Record<string, string>>({});

  const reload = async () => {
    setLoading(true);
    setError(null);
    const { data, error: fnErr } = await supabase.functions.invoke("sheet-tab", {
      body: { action: "load", tab: "media", event_id: eventId },
    });
    setLoading(false);
    if (fnErr || data?.error || data?.denied) {
      setError(data?.message || data?.error || "These requests couldn't be loaded.");
      setRows([]);
      return;
    }
    const all = (data?.rows ?? []) as MediaCredential[];
    const live = all.filter((r) => r.status !== "denied");
    setRows(live);

    const ids = Array.from(new Set(live.map((r: any) => r.reviewed_by).filter(Boolean)));
    if (ids.length) {
      const { data: profs } = await (supabase as any)
        .from("profiles")
        .select("id, first_name, last_name, email")
        .in("id", ids);
      const map: Record<string, string> = {};
      (profs ?? []).forEach((p: any) => {
        map[p.id] = [p.first_name, p.last_name].filter(Boolean).join(" ") || p.email || "a reviewer";
      });
      setReviewers(map);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [eventId]);

  const review = async (id: string, decision: "approved" | "denied", reason?: string) => {
    setBusy(id);
    const { data, error: fnErr } = await supabase.functions.invoke("sheet-tab", {
      body: { action: "media_review", tab: "media", event_id: eventId, id, decision, reason },
    });
    setBusy(null);
    if (fnErr || data?.error) {
      toast.error(data?.error || "Couldn't record that decision.");
      return false;
    }
    if (decision === "approved") toast.success("Approved — the applicant will be emailed shortly");
    else toast.success("Denied");
    await reload();
    return true;
  };

  const deny = async () => {
    if (!denying) return;
    if (!denialReason.trim()) {
      toast.error("A reason is required to deny a request.");
      return;
    }
    const ok = await review(denying.id, "denied", denialReason.trim());
    if (ok) { setDenying(null); setDenialReason(""); }
  };

  const reviewedLabel = (r: any) => {
    const who = r.reviewed_by ? reviewers[r.reviewed_by] || "a reviewer" : "payment";
    const when = r.reviewed_at ? new Date(r.reviewed_at).toLocaleDateString() : null;
    return `Approved by ${who}${when ? ` on ${when}` : ""}`;
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (error) return <div className="p-8 text-center text-sm text-destructive">{error}</div>;
  if (!rows.length) return <div className="p-8 text-center text-sm text-muted-foreground">No media requests yet.</div>;

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="text-left px-3 py-2 w-8" />
            <th className="text-left px-3 py-2">Applicant</th>
            <th className="text-left px-3 py-2">Organization</th>
            <th className="text-left px-3 py-2">Coverage</th>
            <th className="text-left px-3 py-2">Submitted</th>
            <th className="text-right px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const reviewed = r.status === "approved" || r.status === "issued";
            return (
            <>
              <tr key={r.id} className="border-t border-border">
                <td className="px-3 py-2">
                  <button onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                    {expanded === r.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium">{r.name}</div>
                  <div className="text-xs text-muted-foreground">{r.email}</div>
                </td>
                <td className="px-3 py-2">{r.organization || "—"}</td>
                <td className="px-3 py-2">{r.coverage_type || "—"}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {(r as any).created_at ? new Date((r as any).created_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-3 py-2 text-right space-x-1">
                  {reviewed ? (
                    <span className="text-xs text-muted-foreground">{reviewedLabel(r)}</span>
                  ) : (
                    <>
                      <button
                        disabled={busy === r.id}
                        onClick={() => review(r.id, "approved")}
                        className="px-2 py-1 rounded bg-emerald-600 text-white text-xs disabled:opacity-50"
                      >
                        Approve
                      </button>
                      <button
                        disabled={busy === r.id}
                        onClick={() => { setDenying(r); setDenialReason(""); }}
                        className="px-2 py-1 rounded bg-red-600 text-white text-xs disabled:opacity-50"
                      >
                        Deny
                      </button>
                    </>
                  )}
                </td>
              </tr>
              {expanded === r.id && (
                <tr key={r.id + "-x"} className="bg-muted/20 border-t border-border">
                  <td />
                  <td colSpan={5} className="px-3 py-3">
                    <pre className="text-xs whitespace-pre-wrap break-all">{JSON.stringify(r.form_data || {}, null, 2)}</pre>
                  </td>
                </tr>
              )}
            </>
          );})}
        </tbody>
      </table>

      <Dialog open={!!denying} onOpenChange={(o) => !o && setDenying(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Deny credential application</DialogTitle></DialogHeader>
          <div>
            <Label>Reason (required)</Label>
            <textarea rows={3} value={denialReason} onChange={(e) => setDenialReason(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <DialogFooter>
            <button className="px-3 py-2 rounded-md border border-input text-sm" onClick={() => setDenying(null)}>Cancel</button>
            <button className="px-3 py-2 rounded-md bg-red-600 text-white text-sm" onClick={deny}>Deny</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ───── Issued ───── */
export function IssuedTab({ eventId }: { eventId: string }) {
  const [rows, setRows] = useState<MediaCredential[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<"all" | "approved" | "issued" | "revoked">("all");
  const [search, setSearch] = useState("");

  const reload = async () => {
    setLoading(true);
    const { data } = await supabase.from("media_credentials").select("*")
      .eq("event_id", eventId).in("status", ["approved","issued","revoked"]).order("created_at", { ascending: false });
    setRows((data ?? []) as MediaCredential[]);
    setLoading(false);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [eventId]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    const q = search.trim().toLowerCase();
    if (q && !((r.name || "").toLowerCase().includes(q) || (r.organization || "").toLowerCase().includes(q))) return false;
    return true;
  }), [rows, statusFilter, search]);

  const stats = useMemo(() => {
    const issued = rows.filter((r) => r.status === "issued").length;
    const approved = rows.filter((r) => r.status === "approved").length;
    const revoked = rows.filter((r) => r.status === "revoked").length;
    const revenue = rows.reduce((s, r) => s + (r.amount_paid_cents || 0), 0) / 100;
    return { issued, approved, revoked, revenue };
  }, [rows]);

  const setStatus = async (r: MediaCredential, status: MediaCredential["status"]) => {
    const { error } = await supabase.from("media_credentials").update({ status }).eq("id", r.id);
    if (error) toast.error(error.message); else { toast.success("Updated"); reload(); }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-3 rounded-lg border border-border"><div className="text-xs text-muted-foreground uppercase">Issued</div><div className="text-2xl font-bold">{stats.issued}</div></div>
        <div className="p-3 rounded-lg border border-border"><div className="text-xs text-muted-foreground uppercase">Approved (pending payment)</div><div className="text-2xl font-bold">{stats.approved}</div></div>
        <div className="p-3 rounded-lg border border-border"><div className="text-xs text-muted-foreground uppercase">Revoked</div><div className="text-2xl font-bold">{stats.revoked}</div></div>
        <div className="p-3 rounded-lg border border-border"><div className="text-xs text-muted-foreground uppercase">Revenue</div><div className="text-2xl font-bold">${stats.revenue.toFixed(0)}</div></div>
      </div>

      <div className="flex items-center gap-2">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}
          className="h-8 rounded-md border border-input bg-background px-2 text-xs">
          <option value="all">All statuses</option>
          <option value="approved">Approved</option>
          <option value="issued">Issued</option>
          <option value="revoked">Revoked</option>
        </select>
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2 top-2 w-4 h-4 text-muted-foreground" />
          <Input className="pl-8 h-8" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name/organization…" />
        </div>
        <button onClick={() => toast.info("CSV export coming soon")}
          className="px-3 py-1.5 rounded-md border border-input text-sm">Export CSV</button>
      </div>

      {loading ? <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div> :
        filtered.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">No credentials.</div> :
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Applicant</th>
                <th className="text-left px-3 py-2">Organization</th>
                <th className="text-left px-3 py-2">Coverage</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-2"><div className="font-medium">{r.name}</div><div className="text-xs text-muted-foreground">{r.email}</div></td>
                  <td className="px-3 py-2">{r.organization || "—"}</td>
                  <td className="px-3 py-2">{r.coverage_type || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={cn(
                      "inline-flex px-2 py-0.5 rounded-full text-xs",
                      r.status === "issued" ? "bg-emerald-100 text-emerald-800" :
                      r.status === "approved" ? "bg-amber-100 text-amber-800" :
                      "bg-gray-200 text-gray-700"
                    )}>{r.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right space-x-1">
                    {r.status === "issued" && (
                      <button onClick={() => setStatus(r, "revoked")} className="px-2 py-1 rounded border border-input text-xs">Revoke</button>
                    )}
                    {r.status === "revoked" && (
                      <button onClick={() => setStatus(r, "issued")} className="px-2 py-1 rounded border border-input text-xs">Re-issue</button>
                    )}
                    {r.status === "approved" && (
                      <button onClick={() => setStatus(r, "issued")} className="px-2 py-1 rounded bg-emerald-600 text-white text-xs">Mark Issued</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    </div>
  );
}
